import { UserId } from "@custom-types/base-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { OpenTeamDetails } from "@hooks/use-team-utils";
import { useToast } from "@hooks/use-toast";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  TeamEvents,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface OpenProfilePage {
  /** The source place from where the profile is opening */
  source: string;

  /** Contains the user id or identity */
  userIdentity?: UserId;
}

interface UseMembersUtils {
  /** Callback function to open profile page of a user */
  openProfilePage(params: OpenProfilePage): void;

  /** Callback function to open team page of a user */
  openViewTeamMembers(params: OpenTeamDetails): void;
}

/**
 * @returns utility functions to open and navigate to member related pages
 */
export function useMembersUtils(): UseMembersUtils {
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const { navigateToMemberProfile, navigateToTeamDetails } = useAppNavigation();
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /**
   * Opens the team details page if the required parameters are available.
   *
   * @param {string} param0.teamId - The unique identifier of the team.
   * @param {number} [param0.numberOfMembers] - The number of members in the team (optional).
   */
  function openViewTeamMembers({
    teamId,
    numberOfMembers,
  }: OpenTeamDetails): void {
    trackEvent({
      name: TeamEvents.openTeam,
      props: {
        teamId,
        numberOfMembers: numberOfMembers ?? "",
      },
    });

    if (companyId) {
      navigateToTeamDetails({
        companyId,
        teamId,
      });
    } else {
      showToast({
        message: `${TEAM_DISPLAY_NAME} profile page cannot be opened`,
        type: "error",
        description:
          "Workspace ID is not available. Please reload the page to try again.",
      });
    }
  }

  function openProfilePage({ source, userIdentity }: OpenProfilePage): void {
    trackEvent({
      name: WorkspaceMemberEvents.openMemberProfilePage,
      props: { source },
    });

    if (companyId && userIdentity) {
      navigateToMemberProfile({
        companyId,
        // Encode memberId since it can be an email address and have special characters
        // That might conflict with the URL special characters
        memberId: encodeURIComponent(userIdentity),
      });
    } else {
      showToast({
        message: "User profile page cannot be opened",
        type: "error",
        description:
          "Workspace ID and/or user ID not available. " +
          "Please reload the page to try again.",
      });
    }
  }

  return {
    openProfilePage,
    openViewTeamMembers,
  };
}
